/** Traducción de señales técnicas del motor ops → lenguaje para dirección / soporte no técnico. */

export const RISK_LABELS = {
    ok: { title: 'Estable', color: '#2d6a4f', summary: 'La plataforma responde con normalidad. No hay alertas que requieran acción inmediata.' },
    warn: {
        title: 'Atención',
        color: '#b8860b',
        summary: 'Hay señales de sobrecarga o lentitud. Conviene revisar en las próximas horas antes de un pico de uso.',
    },
    critical: {
        title: 'Crítico',
        color: '#8b1538',
        summary: 'El servicio puede fallar o volverse muy lento para los usuarios. Se recomienda actuar hoy.',
    },
};

export const FLAG_PLAYBOOK = {
    http_p95_warn: {
        title: 'La aplicación responde más lento de lo habitual',
        meaning: 'La mayoría de las pantallas y acciones tardan más de lo esperado en cargar.',
        actions: [
            'Evitar cargas masivas o reuniones con muchos usuarios conectados a la vez.',
            'Revisar si hay muchas empresas activas simultáneamente.',
            'Coordinar con soporte técnico un reinicio del servidor en horario de baja actividad.',
        ],
    },
    http_p95_critical: {
        title: 'Lentitud severa en la plataforma',
        meaning: 'Los tiempos de espera superan el umbral crítico; los usuarios pueden percibir bloqueos.',
        actions: [
            'Comunicar a los clientes un posible retraso temporal.',
            'Reducir uso intensivo (importaciones Excel, simulaciones masivas) hasta normalizar.',
            'Escalar de inmediato a soporte técnico para revisar servidor y base de datos.',
        ],
    },
    http_p99_warn: {
        title: 'Algunas operaciones tardan muchísimo',
        meaning: 'Un pequeño grupo de acciones (las más pesadas) se demoran de forma notable.',
        actions: [
            'Identificar qué módulos usan más en ese momento (calculadora, diagnóstico, carga de personal).',
            'Espaciar tareas pesadas entre empresas.',
            'Solicitar revisión de consultas lentas al equipo técnico.',
        ],
    },
    http_p99_critical: {
        title: 'Operaciones muy pesadas bloqueando el sistema',
        meaning: 'Las acciones más exigentes están tardando demasiado y arrastran la experiencia general.',
        actions: [
            'Pausar importaciones masivas y pruebas de estrés hasta nueva indicación.',
            'Aumentar capacidad del servidor o dividir carga entre instancias (soporte técnico).',
        ],
    },
    error_rate_warn: {
        title: 'Aumento de errores en pantallas',
        meaning: 'Más de lo normal de peticiones terminan en error (mensajes de fallo o pantallas en blanco).',
        actions: [
            'Anotar qué pantalla y qué empresa reportó el problema.',
            'Verificar que el servidor y la base de datos estén en marcha.',
            'Revisar si hubo un despliegue reciente de cambios.',
        ],
    },
    error_rate_critical: {
        title: 'Muchos errores del servidor',
        meaning: 'Una fracción importante de intentos de uso falla; riesgo alto de quejas de clientes.',
        actions: [
            'Escalar urgente a soporte técnico.',
            'Considerar mensaje interno de “mantenimiento” si los errores persisten más de 15 minutos.',
            'Revisar logs y último cambio publicado en la aplicación.',
        ],
    },
    sqlite_busy_warn: {
        title: 'Base de datos con mucha presión al guardar',
        meaning: 'Muchos usuarios guardan datos al mismo tiempo y el sistema debe reintentar las operaciones.',
        actions: [
            'Espaciar cargas masivas de personal o implementación por empresa.',
            'Evitar que varios administradores importen Excel al mismo tiempo.',
        ],
    },
    sqlite_busy_critical: {
        title: 'Base de datos saturada',
        meaning: 'La cola de escrituras es tan alta que el rendimiento se degrada con frecuencia.',
        actions: [
            'Detener temporalmente importaciones y procesos batch.',
            'Solicitar revisión de capacidad de base de datos y posible ampliación de recursos.',
        ],
    },
    sqlite_write_fail: {
        title: 'No se pudieron guardar algunos datos',
        meaning: 'Por saturación, algunas operaciones de guardado fallaron y pueden requerir reintento.',
        actions: [
            'Pedir al usuario que repita la acción (carga, registro, encuesta).',
            'Verificar integridad de la última importación o registro.',
            'Escalar a soporte si el contador sigue subiendo.',
        ],
    },
    active_requests_warn: {
        title: 'Mucha actividad simultánea',
        meaning: 'Hay muchas personas usando la plataforma al mismo tiempo y el servidor está al límite.',
        actions: [
            'Comunicar a equipos internos que eviten pruebas masivas en horario pico.',
            'Planificar capacitaciones y demos en franjas distintas.',
        ],
    },
    active_requests_critical: {
        title: 'Servidor al límite de capacidad',
        meaning: 'El número de operaciones en curso sugiere riesgo de caída o timeouts generalizados.',
        actions: [
            'Reducir concurrencia de inmediato (menos usuarios conectados a la vez).',
            'Coordinar con infraestructura ampliación temporal de CPU/RAM o segunda instancia.',
        ],
    },
    heap_pressure_warn: {
        title: 'Memoria del servidor algo justa',
        meaning: 'El proceso que ejecuta ALINEA usa gran parte de la memoria disponible.',
        actions: [
            'Programar reinicio del servicio en ventana de mantenimiento.',
            'Revisar si hay procesos duplicados o fugas tras despliegues recientes.',
        ],
    },
    heap_pressure_critical: {
        title: 'Memoria del servidor casi agotada',
        meaning: 'Riesgo de que el servicio se cierre solo o deje de responder.',
        actions: [
            'Reinicio controlado del servidor lo antes posible.',
            'Ampliar memoria RAM o migrar a un plan superior en el proveedor cloud.',
        ],
    },
    throughput_collapse: {
        title: 'Caída brusca de rendimiento',
        meaning: 'En los últimos minutos el sistema procesó muchas menos operaciones por segundo que antes.',
        actions: [
            'Comprobar si hubo corte de red, reinicio o pico repentino de usuarios.',
            'Revisar con soporte si un proceso en segundo plano consumió recursos.',
        ],
    },
    degradation_sustained: {
        title: 'La lentitud va empeorando',
        meaning: 'Los tiempos de respuesta suben lectura tras lectura; tendencia negativa.',
        actions: [
            'No esperar a que se estabilice solo: escalar a soporte en la misma jornada.',
            'Documentar hora de inicio y módulos más usados (EPA, diagnóstico, calculadora).',
        ],
    },
};

export function explainFlag(flag) {
    const base = FLAG_PLAYBOOK[flag.code];
    if (base) return { ...base, severity: flag.severity, rawMessage: flag.message };
    return {
        title: 'Señal operativa',
        meaning: flag.message || 'Requiere revisión técnica.',
        actions: ['Contactar al equipo de soporte técnico de ALINEA con captura de esta pantalla.'],
        severity: flag.severity,
        rawMessage: flag.message,
    };
}

export const FRIENDLY_METRICS = [
    { key: 'tenantsActive', label: 'Empresas con personal activo', hint: 'Clientes usando la plataforma hoy.' },
    { key: 'throughputRps', label: 'Operaciones por segundo', hint: 'Ritmo general de uso (más alto = más actividad).' },
    { key: 'p95', label: 'Tiempo de respuesta típico (ms)', hint: 'Cuánto tarda la mayoría de pantallas. Menor es mejor.' },
    { key: 'errorRate', label: 'Errores del servidor', hint: 'Proporción de fallos. Debe mantenerse cerca de 0.' },
    { key: 'activeRequests', label: 'Tareas en curso', hint: 'Cuántas peticiones están procesándose ahora.' },
    { key: 'busyRetries', label: 'Reintentos al guardar', hint: 'Si sube mucho, la base de datos está saturada.' },
    { key: 'writeFails', label: 'Guardados fallidos', hint: 'Datos que no se pudieron persistir; idealmente 0.' },
];
